import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, Chip, CircularProgress, Alert, Dialog, DialogTitle, DialogContent, DialogActions, TextField } from '@mui/material';
import { DataGrid, GridColDef, GridActionsCellItem } from '@mui/x-data-grid';
import { arSD } from '@mui/x-data-grid/locales';
import { Add, Refresh, Edit, Delete } from '@mui/icons-material';
import { getCompanyAuthUsers, updateUser, deleteUser } from '../../lib/api';
import LinkAuthUserDialog from './LinkAuthUserDialog';

interface AuthUser {
  id: string;
  email: string;
  created_at: string;
  last_sign_in_at: string;
  user_metadata: {
    full_name?: string;
    company_id?: string | number;
  };
  app_metadata: {
    role?: string;
  };
}

interface CompanyAuthUsersProps {
  companyId: string | number;
  companyEmail?: string;
  companyName?: string;
  editMode?: boolean;
}

const CompanyAuthUsers: React.FC<CompanyAuthUsersProps> = ({ companyId, companyEmail, companyName, editMode = false }) => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const [users, setUsers] = useState<AuthUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [editUserDialog, setEditUserDialog] = useState<{ open: boolean; user: AuthUser | null }>({
    open: false,
    user: null,
  });
  const [deleteUserDialog, setDeleteUserDialog] = useState<{ open: boolean; user: AuthUser | null }>({
    open: false,
    user: null,
  });
  const [editFormData, setEditFormData] = useState<{ full_name: string }>({ full_name: '' });

  useEffect(() => {
    fetchAuthUsers();
  }, [companyId]);

  const fetchAuthUsers = async () => {
    setLoading(true);
    setError(null);
    try {
      // Use dedicated API endpoint to get auth users for this company
      const companyUsers = await getCompanyAuthUsers(companyId);

      setUsers(companyUsers as AuthUser[]);
    } catch (error: any) {
      console.error('Error fetching auth users:', error);
      setError(error.message || 'Failed to load auth users');
    } finally {
      setLoading(false);
    }
  };

  const handleEditUser = (user: AuthUser) => {
    setEditFormData({ full_name: user.user_metadata?.full_name || '' });
    setEditUserDialog({ open: true, user });
  };

  const handleDeleteUser = (user: AuthUser) => {
    setDeleteUserDialog({ open: true, user });
  };

  const handleConfirmDelete = async () => {
    if (!deleteUserDialog.user) return;

    try {
      await deleteUser(deleteUserDialog.user.id);
      setDeleteUserDialog({ open: false, user: null });
      fetchAuthUsers(); // Refresh the list
    } catch (error: any) {
      setError(error.message || 'Failed to delete user');
    }
  };

  const handleSaveUser = async () => {
    if (!editUserDialog.user) return;

    try {
      const updates = {
        user_metadata: {
          ...editUserDialog.user.user_metadata,
          full_name: editFormData.full_name,
        },
      };

      await updateUser(editUserDialog.user.id, updates);
      setEditUserDialog({ open: false, user: null });
      fetchAuthUsers(); // Refresh the list
    } catch (error: any) {
      setError(error.message || 'Failed to update user');
    }
  };

  const baseColumns: GridColDef[] = [
    { 
      field: 'email', 
      headerName: t('users.email'), 
      flex: 1,
      minWidth: 200,
    },
    { 
      field: 'full_name', 
      headerName: t('users.name'), 
      flex: 1,
      minWidth: 150,
      valueGetter: (_value, row) => row.user_metadata?.full_name || '-',
    },
    { 
      field: 'created_at', 
      headerName: t('users.createdAt'), 
      width: 180,
      valueFormatter: (value) => new Date(value).toLocaleDateString(),
    },
    { 
      field: 'last_sign_in_at', 
      headerName: t('users.lastSignIn'), 
      width: 180,
      valueFormatter: (value) => value ? new Date(value).toLocaleDateString() : '-',
    },
    {
      field: 'role',
      headerName: t('users.role', 'Role'),
      width: 100,
      renderCell: (params) => {
        const role = params.row.app_metadata?.role;
        if (role === 'admin') {
          return (
            <Chip
              label="ADMIN"
              color="warning"
              size="small"
              sx={{ fontWeight: 600, fontSize: '0.7rem' }}
            />
          );
        }
        return (
          <Chip
            label={t('users.user', 'User')}
            color="default"
            size="small"
          />
        );
      },
    },
    {
      field: 'status',
      headerName: t('users.status'),
      width: 120,
      renderCell: (params) => {
        const isActive = params.row.last_sign_in_at;
        return (
          <Chip
            label={isActive ? t('users.active') : t('users.inactive')}
            color={isActive ? 'success' : 'default'}
            size="small"
          />
        );
      },
    },
  ];

  // Add actions column when in edit mode
  const columns: GridColDef[] = editMode 
    ? [
        ...baseColumns,
        {
          field: 'actions',
          type: 'actions',
          headerName: t('common.actions'),
          width: 120,
          getActions: (params) => [
            <GridActionsCellItem
              key="edit"
              icon={<Edit />}
              label={t('common.edit')}
              onClick={() => handleEditUser(params.row)}
            />,
            <GridActionsCellItem
              key="delete"
              icon={<Delete color="error" />}
              label={t('common.delete')}
              onClick={() => handleDeleteUser(params.row)}
            />,
          ],
        },
      ]
    : baseColumns;

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={3}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
        <Button size="small" onClick={fetchAuthUsers} sx={{ ms: 2 }}>
          {t('common.retry')}
        </Button>
      </Alert>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Chip label={`${t('common.total')}: ${users.length}`} size="small" color="primary" />
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            size="small"
            onClick={fetchAuthUsers}
            variant="outlined"
            sx={{ gap: 1 }}
          >
            <Refresh sx={{ fontSize: 18 }} />
            {t('common.refresh')}
          </Button>
          <Button
            size="small"
            variant={editMode ? "contained" : "outlined"}
            color={editMode ? "primary" : "inherit"}
            onClick={() => setLinkDialogOpen(true)}
            sx={{ gap: 1 }}
          >
            <Add sx={{ fontSize: 18 }} />
            {t('companies.addUser', 'Add User')}
          </Button>
        </Box>
      </Box>

      <Box sx={{ height: 400 }}>
        <DataGrid
          rows={users}
          columns={columns}
          pageSizeOptions={[5, 10, 25]}
          initialState={{
            pagination: { paginationModel: { pageSize: 10 } },
          }}
          disableRowSelectionOnClick
          localeText={isRTL ? arSD.components.MuiDataGrid.defaultProps.localeText : undefined}
          sx={{
            direction: isRTL ? 'rtl' : 'ltr',
          }}
        />
      </Box>

      <LinkAuthUserDialog
        open={linkDialogOpen}
        onClose={() => setLinkDialogOpen(false)}
        companyId={companyId}
        companyEmail={companyEmail}
        companyName={companyName}
        onUserLinked={fetchAuthUsers}
      />

      {/* Edit User Dialog */}
      <Dialog 
        open={editUserDialog.open} 
        onClose={() => setEditUserDialog({ open: false, user: null })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t('users.editUser', 'Edit User')}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label={t('users.name')}
            value={editFormData.full_name}
            onChange={(e) => setEditFormData({ ...editFormData, full_name: e.target.value })}
            margin="normal"
          />
          <TextField
            fullWidth
            label={t('users.email')}
            value={editUserDialog.user?.email || ''}
            margin="normal"
            disabled
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditUserDialog({ open: false, user: null })}>
            {t('common.cancel')}
          </Button>
          <Button variant="contained" onClick={handleSaveUser}>
            {t('common.save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete User Dialog */}
      <Dialog 
        open={deleteUserDialog.open} 
        onClose={() => setDeleteUserDialog({ open: false, user: null })}
      >
        <DialogTitle>{t('common.confirmDelete')}</DialogTitle>
        <DialogContent>
          {t('users.confirmDeleteUser', 'Are you sure you want to remove this user from the company?')}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteUserDialog({ open: false, user: null })}>
            {t('common.cancel')}
          </Button>
          <Button 
            variant="contained" 
            color="error" 
            onClick={handleConfirmDelete}
          >
            {t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CompanyAuthUsers;
